import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface VolcesSeedream45Params {
  /**
   * 文本提示词，用于描述要生成的图片内容
   */
  prompt: string;
  /**
   * 输入图片，支持 URL 地址或 Base64 编码的图片数据。字符串格式用于图生图，数组格式用于批量生成（最多14张）
   */
  image?: string | string[];
  /**
   * 序列图片生成模式。可选值："disabled"（生成单张图片，默认）或 "auto"（生成多张相关图片）
   * @default "disabled"
   * @values disabled | auto
   */
  sequential_image_generation?: 'disabled' | 'auto';
  /**
   * 序列图片生成选项，仅当 sequential_image_generation 为 "auto" 时生效
   */
  sequential_image_generation_options?: {
  };
  /**
   * 是否使用流式传输，默认值为 false（在批量生成时有用）
   * @default false
   */
  stream?: boolean;
  /**
   * 图片尺寸，例如："2K"、"1024x1024" 等
   * @values 2K | 4K | 2048x2048 | 2304x1728 | 1728x2304 | 2560x1440 | 1440x2560 | 2496x1664 | 1664x2496 | 3024x1296
   */
  size?: '2K' | '4K' | '2048x2048' | '2304x1728' | '1728x2304' | '2560x1440' | '1440x2560' | '2496x1664' | '1664x2496' | '3024x1296';
  /**
   * 是否添加水印，默认值为 false
   * @default false
   */
  watermark?: boolean;
  /**
   * 提示词优化选项
   */
  optimize_prompt_options?: {
    /**
     * 提示词优化模式
     * @values standard | fast
     */
    mode: 'standard' | 'fast';
  };
}

export interface VolcesSeedream45Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seedream_4_5
 *
 * <p>API Documentation: https://docs.byteplus.com/en/docs/ModelArk/1541523</p>
 * <p>Volces Seedream 4.5 支持文生图、图生图和批量生成三种模式。</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeedream45 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeedream45({
 *   // 参数见 VolcesSeedream45Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeedream45Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeedream45(
  params: VolcesSeedream45Params,
): Promise<VolcesSeedream45Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seedream_4_5',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeedream45Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
